import { useEffect, useRef, useState, useCallback } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import './TopMoversWidget.css';
import { TopMoversDataType } from '../../types/rank.type';
import TopMoversTable from '../../Components/TopMoversTable/TopMoversTable';
import { getTopMoversData } from '../../api/rank.api';
import {
  RankType,
  Geography,
  Region,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import { StockData } from '../../types/indices.type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

export interface TopMoversWidgetProps extends BaseWidgetProps {
  token?: string;
  geography?: Geography;
  rankType?: RankType;
  onCallback?: (message: StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const TopMoversWidget = ({
  theme,
  token,
  geography = Geography.ASX,
  rankType = RankType.PricePercentageGain,
  onLoadingChange,
  onError,
  onCallback,
  onNoData,
  onHeightChange,
}: TopMoversWidgetProps) => {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.TopMovers, onHeightChange);

  const { isSmallScreen, isLargeScreen } = useContainerWidth(widgetRef);

  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [data, setData] = useState<TopMoversDataType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'topMovers');

  useEffect(() => {
    fetchTopMoversData();
  }, [token, geography, rankType]);

  const fetchTopMoversData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await getTopMoversData({
        geography: geography,
        rankType: rankType,
        token: token || '',
      });
      if (res.data.length === 0) {
        setIsEmptyData(true);
        onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'TopMoversWidget',
          data: null,
        });
      } else {
        setData(res.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Top Movers data');
      console.error('Error fetching Top Movers data:', err);
      onError?.({ message: err.message, type: err.name + '_' + err.code });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [token, geography, rankType]);

  const handleOnClick = (symbol: string) => {
    if (onCallback && symbol) {
      let message = {
        data: { symbol: symbol },
        source: 'topmovers',
        type: WidgetMessageType.OnStockClick,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      onCallback(message);
    }
  };
  return (
    <Layout theme={theme || ThemeType.Direct}>
      <div ref={widgetRef} className="top-movers-container">
        {isLoading && !onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="top-movers">
            <div className="top-movers-table-container">
              {isLoading ? (
                <div className="top-movers-loading-container">
                  <div className="top-movers-loading-spinner">Loading...</div>
                </div>
              ) : (
                <TopMoversTable
                  isLoading={isLoading}
                  isSmallScreen={isSmallScreen}
                  isLargeScreen={isLargeScreen}
                  data={data}
                  rankType={rankType}
                  token={token}
                  onStockClick={onCallback ? handleOnClick : undefined}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TopMoversWidget;
